/// <amd-module name="Widgets/CumulativeFlowDiagram/CumulativeFlowDiagramWidget" />
import { BoardColumnsQuery, Column } from "Analytics/Boards/CumulativeFlowDiagramQueries/BoardColumnsQuery";
import { CFDSamplePoint, CumulativeFlowHistoryQuery } from "Analytics/Boards/CumulativeFlowDiagramQueries/CumulativeFlowHistoryQuery";
import * as AnalyticsResources from "Analytics/Common/Resources";
import { AnalyticsExceptionType, recognizeAnalyticsException } from "Analytics/Common/Utilities/AnalyticsExceptionUtilities";
import { ICacheableQueryService, IDashboardsCacheableQueryServiceName } from "Dashboards/WidgetCommon/DashboardsCacheableQueryService";
import { onWidgetLoaded } from "Dashboards/WidgetCommon/WidgetTelemetry";
import { LoadingStatus, WidgetProps } from "Dashboards/WidgetContracts/WidgetProps";
import * as React from "react";
import DataQualityService from "Reporting/DataQuality/DataQualityService";
import { showActionRequiredDialog } from "Reporting/Lightbox/Lightbox";
import { ScalarHorizontalComponent } from "Reporting/Visuals/LayoutComponents/Widgets/ScalarComponent";
import { VisualHeader } from "Reporting/Visuals/VisualHeader";
import { ActionRequiredContent } from "Reporting/Visuals/VisualMessages/ActionRequiredContent";
import { AxFaultInMessage } from "Reporting/Visuals/VisualMessages/AxFaultInMessage";
import { NoDataMessage } from "Reporting/Visuals/VisualMessages/NoDataMessage";
import { UnconfiguredMessage } from "Reporting/Visuals/VisualMessages/UnconfiguredMessage";
import { ICancelablePromise } from "VSS/Core/Util/Promise";
import { format } from "VSS/Core/Util/String";
import { PureChart } from "VSS/Features/Charts/Chart";
import { ChartProps } from "VSS/Features/Charts/Contracts";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { VssComponent } from "VSS/Platform/Layout";
import * as Resources from "Widgets/CumulativeFlowDiagram/Resources";
import { createBoardUrl } from "./BoardsUtils";
import { getAverageWip } from "./CfdAggregationHelper";
import { ChartColorPalettes } from "./ChartColorPalettes";
import { CumulativeFlowDiagramChartPropsProvider } from "./CumulativeFlowDiagramChartPropsProvider";
import { CumulativeFlowDiagramSettings, RollingTimePeriod, StartDateTimePeriod } from "./CumulativeFlowDiagramContracts";
import { ConfigProps, CumulativeFlowDiagramSettingsManager } from "./CumulativeFlowDiagramSettingsManager";
import { dateStringAsDate, extractStartDate, fetchTodayInAccountTimeZone } from "./DateUtils";
import { PlaceholderBoardUnsupportedError } from "./PlaceholderBoardUnsupportedError";
import { getCommandName } from "./QueryUtilities";
import { UnvisitedBoardMessage } from "./UnvisitedBoardMessage";

interface CumulativeFlowDiagramWidgetState {
    /**
     * Indicates the state/mode the widget is currently operating in.
     * The widget code checks for the existence of 'renderData'. When undefined, that means the widget is still loading.
     * When 'renderData' is defined, the code checks the 'kind' prop which describes what experience the widget
     * should render and what data set is contained within the 'renderData' object.
     */
    renderData?:
        | RenderData
        | UnconfiguredRenderData
        | UnvisitedBoardMessageRenderData
        | AXNotReadyMessageRenderData
        | ErrorRenderData
        | MissingAXPermsData;
}

/**
 * This interface describes the data required for the widget to render its experience.
 * Coincidentally it is very similar to the interface for LayoutState used by Burndown/Burnup.
 */
interface RenderData {
    kind: "WidgetRenderData";
    title: string;
    subtitle: string;
    chartProps: ChartProps;
    dataQualityWarnings?: string[];
    wipMetricValue?: number;
}

/** Indicates that the widget needs to be configured. */
interface UnconfiguredRenderData {
    kind: "UnconfiguredRenderData";
}

/**
 * Indicates that the widget should show the "Unvisited board" call-to-action experience on the widget
 * and describes the data required for showing the experience.
 */
interface UnvisitedBoardMessageRenderData {
    kind: "UnvisitedBoardMessageRenderData";
    boardName: string;
    boardUrl: string;
}

/** Indicates that the widget should render the AX Fault-In experience. */
interface AXNotReadyMessageRenderData {
    kind: "AXFaultInRenderData";
}

/** Indicates that the widget is in an error state with the given error. */
interface ErrorRenderData {
    kind: "ErrorRenderData";
    error: any;
}

/** Indicates that the widget is in an error state with the given error. */
interface MissingAXPermsData {
    kind: "MissingAXPermsData";
    error: any;
}

/** A chart that presents a cumulative flow diagram for a configured team board. */
class CumulativeFlowDiagramWidget extends VssComponent<WidgetProps, CumulativeFlowDiagramWidgetState> {
    /**
     * Space allocation reserved for the header that cannot be used by the chart.
     * 22 for title, 16 for subheader, 10 for widget top margin, 10 for header bottom padding.
     */
    private static readonly chartHostHeightAdjustment: number = 58;
    private settingsCancelable: ICancelablePromise<{ title: string; settings: CumulativeFlowDiagramSettings }> | undefined;
    private boardColumnsCancelable: ICancelablePromise<{ boardColumns: Column[]; boardCategoryReferenceName: string }> | undefined;
    private cumulativeFlowHistoryCancelable: ICancelablePromise<CFDSamplePoint[]> | undefined;

    constructor(props: WidgetProps) {
        super(props);

        this.state = {};
    }

    public async componentDidMount(): Promise<void> {
        await this.updateState();
    }

    public async componentDidUpdate(prevProps: WidgetProps, prevState: CumulativeFlowDiagramWidgetState): Promise<void> {
        if (prevState.renderData == null && this.state.renderData != null) {
            if (this.state.renderData.kind === "ErrorRenderData") {
                this.props.onWidgetLoaded({
                    outcome: LoadingStatus.failure,
                    outcomeDescription: this.state.renderData.error
                });
            } else {
                this.props.onWidgetLoaded();
                this.publishOnWidgetLoadedTelemetry();
            }
        }

        if (
            this.props.settings !== prevProps.settings ||
            this.props.size.height !== prevProps.size.height ||
            this.props.size.width !== prevProps.size.width ||
            this.props.name !== prevProps.name
        ) {
            await this.updateState();
        }
    }

    public render(): JSX.Element | null {
        if (this.state.renderData == null || this.state.renderData.kind === "ErrorRenderData") {
            return null;
        }

        let content: JSX.Element | null = null;
        switch (this.state.renderData.kind) {
            case "WidgetRenderData":
                content =
                    this.state.renderData.chartProps.series.length > 0 ? (
                        <>
                            <div className="cumulative-flow-diagram-header">
                                <VisualHeader
                                    title={this.state.renderData.title}
                                    hideTitle={this.props.shouldNotShowTitle}
                                    subtitle={this.state.renderData.subtitle}
                                    dataQualityWarning={DataQualityService.evaluate(this.state.renderData.dataQualityWarnings)}
                                />
                                {this.renderMetrics()}
                            </div>
                            <PureChart {...this.state.renderData.chartProps} />
                        </>
                    ) : (
                        <NoDataMessage size={this.props.size} visualTitle={this.state.renderData.title} />
                    );
                break;
            case "UnvisitedBoardMessageRenderData":
                content = (
                    <>
                        <VisualHeader title={this.props.name} hideTitle={this.props.shouldNotShowTitle} />
                        <UnvisitedBoardMessage
                            visualTitle={this.props.name}
                            size={this.props.size}
                            boardName={this.state.renderData.boardName}
                            boardUrl={this.state.renderData.boardUrl}
                        />
                    </>
                );
                break;
            case "AXFaultInRenderData":
                content = <AxFaultInMessage size={this.props.size} visualTitle={this.props.name} />;
                break;
            case "MissingAXPermsData":
                content = (
                    <ActionRequiredContent
                        visualTitle={this.props.name}
                        onClick={() => {
                            showActionRequiredDialog(
                                this.context.pageContext,
                                AnalyticsResources.PermissionDeniedTitleText,
                                <FormatComponent format={AnalyticsResources.PermissionDeniedDetailText}>
                                    <a href={AnalyticsResources.PermissionDeniedDetailLinkAddress}>
                                        {AnalyticsResources.PermissionDeniedDetailLinkText}
                                    </a>
                                </FormatComponent>
                            );
                        }}
                    />
                );
                break;
            default:
                <UnconfiguredMessage size={this.props.size} visualTitle={this.props.name} />;
                break;
        }

        return <div className="cumulative-flow-diagram-widget">{content}</div>;
    }

    private renderMetrics(): JSX.Element | null {
        if (this.state.renderData && this.state.renderData.kind === "WidgetRenderData" && this.state.renderData.wipMetricValue != null) {
            return (
                <ScalarHorizontalComponent
                    measure={Resources.WorkInProgressMetricLabel}
                    value={this.state.renderData.wipMetricValue}
                    description={Resources.WorkInProgressMetricAggregationDescription}
                />
            );
        }

        return null;
    }

    private async publishOnWidgetLoadedTelemetry(): Promise<void> {
        if (this.state.renderData && this.state.renderData.kind === "WidgetRenderData") {
            const endDate = await fetchTodayInAccountTimeZone(this.context.pageContext);
            const settingsManager = new CumulativeFlowDiagramSettingsManager(this.context.pageContext, getCommandName(this.props.presentationMode));
            const { settings } = await settingsManager.ensureAndParseSettings(this.props as ConfigProps, endDate);

            onWidgetLoaded(this.context.pageContext, this.props, {
                includeFirstBoardColumn: settings.chartDataSettings.includeFirstBoardColumn,
                numberOfSpecifiedColumns: settings.chartDataSettings.desiredBoardColumnIds && settings.chartDataSettings.desiredBoardColumnIds.length, // Only present when used in reports.
                filteringBySwimlane: !!settings.chartDataSettings.boardLane,
                timePeriodType: settings.chartDataSettings.timePeriod.identifier,
                rollingPeriod:
                    settings.chartDataSettings.timePeriod.identifier === "RollingPeriod" ? settings.chartDataSettings.timePeriod.settings : undefined,
                themeName: settings.themeName
            });
        }
    }

    private cancelDataPromises(): void {
        if (this.settingsCancelable) {
            this.settingsCancelable.cancel();
        }

        if (this.boardColumnsCancelable) {
            this.boardColumnsCancelable.cancel();
        }

        if (this.cumulativeFlowHistoryCancelable) {
            this.cumulativeFlowHistoryCancelable.cancel();
        }
    }

    private async updateState(): Promise<void> {
        try {
            this.setState(await this.getRenderData());
        } catch (e) {
            if (e.isCanceled) {
                return; // Do nothing if the promises were canceled
            }

            this.setState({ renderData: this.getRenderDataFromError(e) });
        }
    }

    private async getRenderData(): Promise<{ renderData: RenderData | UnconfiguredRenderData }> {
        this.cancelDataPromises();

        if (this.props.teamContext == null && this.props.settings == null) {
            return { renderData: { kind: "UnconfiguredRenderData" } };
        }

        const dataQualityWarnings: Set<string> = new Set<string>();
        const commandName = getCommandName(this.props.presentationMode);

        const endDate = await fetchTodayInAccountTimeZone(this.context.pageContext);
        const settingsManager = new CumulativeFlowDiagramSettingsManager(this.context.pageContext, commandName);

        this.settingsCancelable = this.trackPromise(settingsManager.ensureAndParseSettings(this.props as ConfigProps, endDate));

        const { title, settings } = await this.settingsCancelable.promise;

        const queryService = this.context.pageContext.getService<ICacheableQueryService>(IDashboardsCacheableQueryServiceName);

        const columnsQuery = new BoardColumnsQuery(commandName, settings.chartDataSettings.project, settings.chartDataSettings.board);
        this.boardColumnsCancelable = this.trackPromise(queryService.getCacheableQueryResult(columnsQuery));
        this.addDataQualityWarnings(queryService.getDataQualityWarnings(columnsQuery), dataQualityWarnings);

        const startDate = await extractStartDate(this.context.pageContext, settings.chartDataSettings.timePeriod);
        const historyQuery = new CumulativeFlowHistoryQuery(commandName, {
            project: settings.chartDataSettings.project,
            boardId: settings.chartDataSettings.board,
            boardLane: settings.chartDataSettings.boardLane,
            startDate
        });
        this.cumulativeFlowHistoryCancelable = this.trackPromise(queryService.getCacheableQueryResult(historyQuery));
        this.addDataQualityWarnings(queryService.getDataQualityWarnings(historyQuery), dataQualityWarnings);

        const [{ boardColumns, boardCategoryReferenceName }, cumulativeFlowHistory] = await Promise.all([
            this.boardColumnsCancelable.promise,
            this.cumulativeFlowHistoryCancelable.promise
        ]);

        const chartPropsProvider = new CumulativeFlowDiagramChartPropsProvider(this.context.pageContext);
        const onChartClick = chartPropsProvider.createChartClickHandler(
            {
                project: settings.chartDataSettings.project,
                team: settings.chartDataSettings.team,
                boardCategoryReferenceName
            },
            this.props
        );

        return {
            renderData: {
                kind: "WidgetRenderData",
                title,
                subtitle: this.createSubtitle(settings.chartDataSettings.timePeriod),
                chartProps: chartPropsProvider.createChartProps({
                    title,
                    size: {
                        width: this.props.size.width,
                        height: this.props.size.height - CumulativeFlowDiagramWidget.chartHostHeightAdjustment
                    },
                    startDate,
                    endDate,
                    includeFirstBoardColumn: settings.chartDataSettings.includeFirstBoardColumn,
                    desiredBoardColumnIds: settings.chartDataSettings.desiredBoardColumnIds,
                    boardColumns,
                    cumulativeFlowHistory,
                    colorPalette: ChartColorPalettes.getInstance().getPalette(settings.themeName),
                    showLoadAnimations: this.props.showLoadAnimations,
                    onChartClick
                }),
                dataQualityWarnings: Array.from(dataQualityWarnings),
                wipMetricValue: getAverageWip(
                    boardColumns,
                    cumulativeFlowHistory,
                    settings.chartDataSettings.desiredBoardColumnIds || boardColumns.map(x => x.ColumnId)
                ) // Dashboard widgets always have all columns selected
            }
        };
    }

    private getRenderDataFromError(e: any): UnvisitedBoardMessageRenderData | AXNotReadyMessageRenderData | ErrorRenderData | MissingAXPermsData {
        const axException = recognizeAnalyticsException(e);
        if (e instanceof PlaceholderBoardUnsupportedError) {
            return {
                kind: "UnvisitedBoardMessageRenderData",
                boardName: e.board.BoardName,
                boardUrl: createBoardUrl(this.context.pageContext, e.project.name, e.team.id, e.board.BoardName)
            };
        } else if (axException === AnalyticsExceptionType.DataNotReady) {
            return { kind: "AXFaultInRenderData" };
        } else if (axException === AnalyticsExceptionType.MissingAnalyticsPermissions) {
            return { kind: "MissingAXPermsData", error: e };
        } else {
            return { kind: "ErrorRenderData", error: e };
        }
    }

    private createSubtitle(timePeriod: RollingTimePeriod | StartDateTimePeriod): string {
        let subtitle: string;
        if (timePeriod.identifier === "RollingPeriod") {
            subtitle =
                timePeriod.settings === 1
                    ? Resources.Subtitle_RollingPeriod_Singular
                    : format(Resources.Subtitle_RollingPeriod_Plural_Format, timePeriod.settings);
        } else {
            const localDate = dateStringAsDate(timePeriod.settings);
            subtitle = format(Resources.Subtitle_StartDate_Format, localDate.toLocaleDateString());
        }
        return subtitle;
    }

    private addDataQualityWarnings(dataQualityPromise: Promise<string[]>, dataQualityWarnings: Set<string>): void {
        dataQualityPromise.then(res => {
            res.forEach(warning => {
                dataQualityWarnings.add(warning);
            });
        });
    }
}

VssComponent.register("cumulativeFlowDiagramWidget", CumulativeFlowDiagramWidget);
